import { useEffect, useId, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";

import { searchAddresses, type AddressSuggestion, type Coord } from "@/lib/geo";

interface Props {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (label: string, coord: Coord) => void;
}

export function AddressAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  onSelect,
}: Props) {
  const id = useId();
  const [results, setResults] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const skipNext = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    if (value.trim().length < 3) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      searchAddresses(value, controller.signal)
        .then((res) => {
          setResults(res);
          setOpen(true);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={boxRef} className="relative">
      <label htmlFor={id} className="field-label">
        {label} <span className="text-destructive">*</span>
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          className="field-input pr-10"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          maxLength={200}
          autoComplete="off"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <MapPin className="h-4 w-4" aria-hidden />
          )}
        </span>
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-2xl border-[1.5px] border-input bg-card p-1.5 shadow-lg">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="flex min-h-12 w-full items-start gap-2 rounded-xl px-3 py-3 text-left text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                onClick={() => {
                  skipNext.current = true;
                  onSelect(r.label, r.coord);
                  setOpen(false);
                  setResults([]);
                }}
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
                <span className="leading-snug">
                  <span className="block">{r.titulo}</span>
                  {r.detalle && (
                    <span className="block text-xs font-medium text-muted-foreground">
                      {r.detalle}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-[11px] font-semibold text-muted-foreground">
        Búsqueda gratuita con OpenStreetMap · solo México
      </p>
    </div>
  );
}
